import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import ApiService from '../../services/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [contactContent, setContactContent] = useState({
    heroTitle: 'Contact Us',
    heroSubtitle: "We're Here to Help",
    email: 'cs@jobitzm.com',
    phone: '+260 76 686 9819',
    address: 'Lusaka, Zambia',
  });

  useEffect(() => {
    let mounted = true;
    const overlay = (next) => {
      const cc = next?.contact || {};
      const gen = next?.general || {};
      setContactContent((prev) => ({
        ...prev,
        heroTitle: cc.heroTitle ?? prev.heroTitle,
        heroSubtitle: cc.heroSubtitle ?? prev.heroSubtitle,
        email: cc.email ?? gen.contactEmail ?? prev.email,
        phone: cc.phone ?? gen.supportPhone ?? prev.phone,
        address: cc.address ?? prev.address,
      }));
    };

    const load = async () => {
      try {
        const resp = await ApiService.getSystemSettings();
        if (!mounted) return;
        overlay(resp || {});
        try { localStorage.setItem('systemSettings', JSON.stringify(resp)); } catch (_) {}
        try { localStorage.setItem('adminSettings', JSON.stringify(resp)); } catch (_) {}
      } catch (_) {
        try {
          const raw = localStorage.getItem('systemSettings') || localStorage.getItem('adminSettings');
          const parsed = raw ? JSON.parse(raw) : null;
          if (mounted && parsed) overlay(parsed);
        } catch (_) {}
      }
    };
    load();
    // Listen for admin settings updates
    const onSettingsUpdated = (e) => {
      const detail = e?.detail || null;
      if (!detail) return;
      if (!mounted) return;
      overlay(detail);
    };
    try { window.addEventListener('systemSettingsUpdated', onSettingsUpdated); } catch (_) {}
    return () => { mounted = false; };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    setSnackbar({
      open: true,
      message: 'Thank you for your message. We will get back to you soon!',
      severity: 'success',
    });
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: '',
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const contactInfo = [
    {
      icon: <EmailIcon fontSize="large" color="primary" />,
      title: 'Email',
      content: `${contactContent.email} | jobitzm@gmail.com`,
    },
    {
      icon: <PhoneIcon fontSize="large" color="primary" />,
      title: 'Phone',
      content: contactContent.phone,
    },
    {
      icon: <LocationIcon fontSize="large" color="primary" />,
      title: 'Address',
      content: contactContent.address,
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box textAlign="center" mb={6}>
        <Typography variant="h3" component="h1" gutterBottom>
          {contactContent.heroTitle}
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph>
          {contactContent.heroSubtitle}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Box>
            {contactInfo.map((info, index) => (
              <Card key={`contact-info-${index}`} sx={{ mb: 2 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  {info.icon}
                  <Typography variant="h6" component="h3" mt={2}>
                    {info.title}
                  </Typography>
                  <Typography color="text.secondary">{info.content}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="h2" gutterBottom>
                Send us a Message
              </Typography>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                    >
                      Send Message
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Contact;